import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogIn, Eye, EyeOff } from "lucide-react";
import { AuthLayout } from "@/components/AuthLayout";
import { useAuth } from "@/context/AuthContext";
import { ROLE_HOME } from "@/constants/roles";
import { AUTH } from "@/constants/testIds";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password, remember);
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.first_name}!`);
      // Check for pending storefront intent (Buy Now / Add to Cart from public page)
      const intentRaw = sessionStorage.getItem("post_login_action");
      const nextParam = new URLSearchParams(location.search).get("next");
      if (intentRaw) {
        try {
          const intent = JSON.parse(intentRaw);
          sessionStorage.removeItem("post_login_action");
          const { api } = await import("@/lib/api");
          if (intent.type === "buy_now" || intent.type === "add_to_cart") {
            try { await api.post("/learner/cart/add-course", { course_id: intent.course_id }); }
            catch { /* likely already-in-cart, ignore */ }
            window.dispatchEvent(new CustomEvent("cart:updated"));
            navigate(intent.type === "buy_now" ? "/cart" : `/courses/${intent.slug}`, { replace: true });
            return;
          }
        } catch { /* ignore */ }
      }
      if (nextParam && nextParam.startsWith("/")) {
        navigate(nextParam, { replace: true });
        return;
      }
      // If guest cart flow flagged a checkout redirect, honour it.
      if (localStorage.getItem("steward_checkout_redirect") === "1") {
        localStorage.removeItem("steward_checkout_redirect");
        navigate("/cart", { replace: true });
        return;
      }
      const home = ROLE_HOME[res.user.role_code] || "/app/learner";
      const from = location.state?.from;
      navigate(from && from.startsWith("/app") ? from : home, { replace: true });
    } else {
      setError(res.error || "Invalid credentials");
    }
  };

  return (
    <AuthLayout
      title="Log in to Steward Learning Cloud"
      subtitle="Sign in with your Steward account to access your dashboard."
      hint={
        <>New to Steward? <Link to="/signup" data-testid={AUTH.loginToSignup} className="font-semibold text-[#101876] hover:underline">Create an account</Link></>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" data-testid={AUTH.loginRoot}>
        {error && (
          <Alert variant="destructive" data-testid={AUTH.loginError}>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Email or Login ID</Label>
          <Input
            id="email"
            type="text"
            required
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@hospital.com or Dinesh"
            data-testid={AUTH.loginEmail}
          />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Password</Label>
            <Link to="/forgot-password" className="text-xs font-semibold text-[#101876] hover:underline" data-testid={AUTH.loginToForgot}>
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="pr-10"
              data-testid={AUTH.loginPassword}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-500 hover:bg-slate-100"
              tabIndex={-1}
              aria-label={showPassword ? "Hide password" : "Show password"}
              data-testid="login-show-password"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <Checkbox checked={remember} onCheckedChange={setRemember} id="remember" data-testid={AUTH.loginRemember} />
          Remember me on this device
        </label>
        <button
          type="submit"
          disabled={loading}
          className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#101876] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0c135c] disabled:opacity-70"
          data-testid={AUTH.loginSubmit}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
          {loading ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </AuthLayout>
  );
}
